import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { busyNamesQueryOptions, sectorFlowQueryOptions } from '@/features/brokers/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { mostTradedSearchSchema, toBusyNamesQuery } from '@/screens/brokers/search'

/**
 * The `/brokers/most-traded` loader.
 *
 * MAIN: busy names, keyed on the WHOLE search object (`sector`/`period`/
 * `page`/`pageSize`) — the table is the entire page below the filter row.
 * Because it is keyed on search params, the screen reads it with a plain
 * `useQuery` + `keepPreviousData`, not suspense — see docs/route-loading.md,
 * "Search params".
 *
 * SECONDARY, prefetched and not awaited, and only on a REAL navigation
 * (`!preload`) — docs/route-loading.md, "Secondary reads": sector flow at the
 * literal `'today'`, which `MostTradedScreen` only reads to populate the
 * sector filter's menu — a slower or failed read there leaves the menu briefly
 * short, never the page. Most traded's own table is a dense list of stock
 * links, so a hover pass over it must not also fire this extra request for
 * every row the cursor crosses on the way to a click; main data still loads on
 * hover.
 *
 * Its own module so a test can mount it under a root of its own.
 */
export async function loadMostTraded({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = mostTradedSearchSchema.parse(location.search)
  if (!preload) prefetchRouteData(queryClient, sectorFlowQueryOptions('today'))
  // Through `toBusyNamesQuery`, the same picker the screen's own
  // `useBusyNames` call goes through, so a stray URL param cannot split the
  // two into different cache keys — see search.ts's own comment.
  await ensureRouteData(queryClient, busyNamesQueryOptions(toBusyNamesQuery(search)))
}
